import {
    KatAppLayout
} from "@amzn/katal-react";
import * as React from 'react';
import {useEffect} from "react";
import {useLocation} from "react-router-dom";
import TopNavigation from "src/app/component/layout/TopNavigation";
import LeftNavigation from "src/app/component/layout/LeftNavigation";
import Footer from "src/app/component/layout/Footer";
import Routes from "src/app/router/Routes";
import {Provider} from "mobx-react";
import reviewDecisionStore from "src/app/store/ReviewDecisionStore";
import userInfoStore from "src/app/store/UserInfoStore";
import {EEPApi} from "src/app/api/EEPApi";
import sessionStore from "src/app/store/SessionStore";
import {isProdStage} from "src/app/constant/Constants";

/**
 * Root application component.
 *
 * Authentication is handled at the API Gateway level by ALI (Amazon Lambda Interceptor).
 * User identity is fetched from the /whoami backend endpoint which reads ALI's principalId.
 */
const App = () => {
    // Case Audit is embedded standalone (loaded directly via its iFrame URL), so it renders
    // bare — without the portal chrome (top app bar, left nav, footer). The review portal
    // keeps the full KatAppLayout.
    const location = useLocation();
    const isStandalone = location.pathname.startsWith('/case-audit') ||
        (!isProdStage() && location.pathname.startsWith('/lighthouse-review'));

    useEffect(() => {
        EEPApi.whoAmI().then(alias => {
            if (alias) {
                userInfoStore.updateValue(alias);
            }
        });
    }, []);

    // When any page is rendered inside the MCExecute iframe, tell the parent we're ready so MC
    // completes its handshake (isIframeLoaded=true) and stops re-pointing the iframe src with a new
    // ?ref every ~20s (the continuous reload loop). Route-agnostic: fires for whichever page MC
    // loads (Lighthouse review, legacy review detail, case audit). No-op outside an iframe
    // (standalone portal use has window === window.parent). Mirrors the windowStatus:"Ready"
    // contract of GrokExceptionResolutionEventManagerLibrary; targetOrigin '*' (MC validates on its
    // side; the payload carries no data).
    useEffect(() => {
        if (window !== window.parent) {
            window.parent.postMessage({type: 'windowStatus', value: {windowStatus: 'Ready'}}, '*');
        }
    }, []);

    const content = isStandalone ? (
        <Routes data-testid='routes'/>
    ) : (
        <KatAppLayout standalone>
            <TopNavigation data-testid='top-nav'/>
            <LeftNavigation data-testid='left-nav'/>
            <Routes data-testid='routes'/>
            <Footer data-testid='footer'/>
        </KatAppLayout>
    );

    return (
        <Provider reviewDecisionStore={reviewDecisionStore} userInfoStore={userInfoStore}
                  sessionStore={sessionStore}>
            {content}
        </Provider>
    );
};

export default App;
